// ボス動画クリップ: atlas(1枚png+json) をロードし、状態→クリップ解決とカーソル→frame index を計算。
// 設計: spec §3.2/§4.6 + Phase1 計画 (Codex 精査反映)。クリップは root-locked。

import type { BossState } from './types'

export type BossClipFrame = {
  file: string; x: number; y: number; w: number; h: number
  footX: number; footY: number; durationMs: number; eventTags: string[]
  // 前進プロファイル (決定①-A): 抽出器が重心xから記録した「中央寄せで捨てた横移動」。
  //   offsetX     = frame0 起点の符号付き移動量 (scaled px・canvas 座標系)
  //   forwardNorm = 移動方向に対する 0→1 正規化 (motion-warp で boss.x 補間カーブに使う)
  offsetX: number; forwardNorm: number
}
export type ClipEvents = { activeStartFrame: number; activeEndFrame: number; recoverEndFrame: number }
export type ClipLoop = 'none' | 'pingpong' | 'window'
export type BossClipMeta = {
  clip: string; outFps: number; loop: ClipLoop
  canvas: [number, number]; footlineFrac: number
  charHeight: number   // キャラ標準高(px)。 表示時 displayHeight に合わせる固定スケール基準
  travelXPx: number    // クリップ全体の符号付き前進量 (scaled px)。 boss.x 駆動の規模 (①-B)
  frames: BossClipFrame[]; events: ClipEvents
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

export function parseClipMeta(raw: any): BossClipMeta {
  const frames: BossClipFrame[] = (raw.frames ?? []).map((f: any) => ({
    file: f.file, x: f.x ?? 0, y: f.y ?? 0, w: f.w, h: f.h,
    footX: f.footX, footY: f.footY, durationMs: f.durationMs, eventTags: f.eventTags ?? [],
    offsetX: typeof f.offsetX === 'number' ? f.offsetX : 0,
    forwardNorm: typeof f.forwardNorm === 'number' ? f.forwardNorm : 0,
  }))
  const last = Math.max(0, frames.length - 1)
  const e = raw.events ?? {}
  const dStart = frames.length ? Math.round(frames.length * 0.45) : 0
  const dEnd = frames.length ? Math.round(frames.length * 0.7) : 0
  const aStart = clamp(typeof e.activeStartFrame === 'number' ? e.activeStartFrame : dStart, 0, last)
  const aEnd = clamp(typeof e.activeEndFrame === 'number' ? e.activeEndFrame : dEnd, aStart, last)
  const rEnd = clamp(typeof e.recoverEndFrame === 'number' ? e.recoverEndFrame : last, aEnd, last)
  const loop: ClipLoop = ['none', 'pingpong', 'window'].includes(raw.loop) ? raw.loop : 'none'
  const charHeight = typeof raw.charHeight === 'number' && raw.charHeight > 0
    ? raw.charHeight
    : (frames.length ? Math.max(...frames.map((f) => f.h)) : 1)
  const travelXPx = typeof raw.travelXPx === 'number' ? raw.travelXPx : 0
  return {
    clip: raw.clip, outFps: raw.outFps, loop,
    canvas: [raw.canvas[0], raw.canvas[1]], footlineFrac: raw.footlineFrac, charHeight, travelXPx,
    frames, events: { activeStartFrame: aStart, activeEndFrame: aEnd, recoverEndFrame: rEnd },
  }
}

export function clipDurationMs(m: BossClipMeta): number {
  return m.frames.length * (1000 / m.outFps)
}

export type BossClipQuery = { state: BossState; currentAttackId: string | null; hp: number }

// hp<=0 は最優先 defeat。 攻撃系 state は attack:<id> に丸める (id 無しは idle)。
// parried は state でなく割り込み (sprites.ts) ゆえここでは返さない。
export function resolveBossClipKey(q: BossClipQuery): string {
  if (q.hp <= 0) return 'defeat'
  switch (q.state) {
    case 'telegraph': case 'active': case 'comboGap': case 'recovery':
      return q.currentAttackId ? `attack:${q.currentAttackId}` : 'idle'
    case 'staggered': return 'staggered'
    case 'idle': default: return 'idle'
  }
}

// クリップキー (runtime, ':'区切り) → ディレクトリ/ファイル名 ('_'区切り)。 全コロン置換。
export function keyToDir(key: string): string { return key.split(':').join('_') }

export type ClipCursor =
  | { mode: 'loop'; elapsedMs: number }
  | { mode: 'oneshot'; elapsedMs: number }
  | { mode: 'segment'; seg: 'telegraph' | 'active' | 'recovery' | 'comboGap'; progress: number }

export function clipFrameIndex(m: BossClipMeta, cur: ClipCursor): number {
  const n = m.frames.length
  if (n === 0) return 0
  const last = n - 1
  const frameDur = 1000 / m.outFps
  if (cur.mode === 'loop') {
    const raw = Math.floor(cur.elapsedMs / frameDur)
    if (m.loop === 'pingpong' && n > 2) {
      const period = (n - 1) * 2
      const p = ((raw % period) + period) % period
      return p < n ? p : period - p
    }
    return ((raw % n) + n) % n
  }
  if (cur.mode === 'oneshot') return clamp(Math.floor(cur.elapsedMs / frameDur), 0, last)
  // segment
  const { activeStartFrame: aS, activeEndFrame: aE, recoverEndFrame: rE } = m.events
  const p = clamp(cur.progress, 0, 1)
  switch (cur.seg) {
    case 'telegraph': return clamp(Math.round(p * aS), 0, aS)
    case 'active': return clamp(aS + Math.round(p * (aE - aS)), aS, aE)
    case 'recovery': return clamp(aE + Math.round(p * (rE - aE)), aE, rE)
    case 'comboGap': default: return aE
  }
}

// ===== atlas ロード (ブラウザのみ) =====
// atlas = video_to_sprites.py --pack atlas が吐く <clip>.png(全フレーム横連結) + <clip>.json。
// 各セルは「フルキャンバス(canvas[cw,ch])」で、 キャラの足元が (footX,footY) に揃っている。
// charH = キャラ標準高 (bbox h の最大) → 描画側で displayHeight に合わせる固定スケールに使う。
export type LoadedBossClip = { meta: BossClipMeta; image: HTMLImageElement; charH: number }

export async function loadBossClip(bossId: string, key: string): Promise<LoadedBossClip | null> {
  if (typeof Image === 'undefined' || typeof fetch === 'undefined') return null
  const dir = `/sprites/bosses/${bossId}/clips/${keyToDir(key)}`
  try {
    const res = await fetch(`${dir}/${keyToDir(key)}.json`)
    if (!res.ok) return null
    const meta = parseClipMeta(await res.json())
    if (meta.frames.length === 0) return null
    const image = new Image()
    image.src = `${dir}/${keyToDir(key)}.png`
    return { meta, image, charH: meta.charHeight }
  } catch {
    return null
  }
}
